import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Alert,
  Stack,
  TextField,
  CircularProgress,
} from '@mui/material';
import ReplayIcon from '@mui/icons-material/Replay';
import LaunchIcon from '@mui/icons-material/Launch';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { api } from '../api/client.js';
import { useT } from '../i18n/useT.js';

/**
 * "Re-run" — turns the session into a runnable scenario link. Opening the link
 * on the recorded site makes the SDK fetch the compiled steps and (after an
 * explicit confirm shown by the SDK itself) replay them. The link piggybacks
 * on the public-share token, so revoking the share revokes the re-run too.
 */
export function RerunDialog({
  open,
  onClose,
  orgId,
  projectId,
  sessionId,
  entryUrl,
  stepsCount,
}: {
  open: boolean;
  onClose: () => void;
  orgId?: string;
  projectId?: string;
  sessionId?: string;
  entryUrl?: string;
  stepsCount: number;
}) {
  const all = useT();
  const t = all.replay.rerun;
  const common = all.common;
  const [copied, setCopied] = useState(false);
  const base = `/v1/orgs/${orgId}/projects/${projectId}/sessions/${sessionId}`;

  const share = useQuery({
    enabled: open && !!orgId && !!projectId && !!sessionId,
    queryKey: ['rerun-link', sessionId],
    queryFn: async () => (await api.post(`${base}/share`)).data as { token: string },
  });

  const link =
    share.data && entryUrl
      ? `${entryUrl}#ro_rerun=${encodeURIComponent(window.location.origin)}|${share.data.token}`
      : null;

  const copy = async () => {
    if (!link) return;
    await navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <Stack direction="row" spacing={1} alignItems="center">
          <ReplayIcon color="primary" />
          <span>{t.title}</span>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" sx={{ mb: 1.5 }}>
          {t.body(stepsCount)}
        </Typography>
        <Alert severity="info" sx={{ mb: 2 }}>
          {t.maskedNote}
        </Alert>
        {share.isPending && (
          <Stack alignItems="center" sx={{ py: 2 }}>
            <CircularProgress size={22} />
          </Stack>
        )}
        {share.isError && <Alert severity="error">{t.failed}</Alert>}
        {link && (
          <TextField
            fullWidth
            size="small"
            value={link}
            slotProps={{ input: { readOnly: true, sx: { fontFamily: 'ui-monospace, monospace', fontSize: 12 } } }}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{common.close}</Button>
        <Button startIcon={<ContentCopyIcon />} disabled={!link} onClick={copy}>
          {copied ? t.copied : t.copyLink}
        </Button>
        <Button
          variant="contained"
          startIcon={<LaunchIcon />}
          disabled={!link}
          onClick={() => window.open(link!, '_blank', 'noopener')}
        >
          Open & run
        </Button>
      </DialogActions>
    </Dialog>
  );
}
